/**
 * Button Component Definition
 *
 * A clickable button/CTA with full styling options.
 */

import { colorValueToCss, colorValueToSolidCss } from '../../fields/ColorPicker/colorUtils';
import { getResponsiveDimension } from '../../renderer/StyleResolver';
import { getFontFamilyCSS } from '../../../utils/googleFonts';

export const buttonComponent = {
  type: 'button',
  label: 'Button',
  icon: 'MousePointer',
  description: 'Clickable button or call-to-action',
  category: 'content',

  canHaveChildren: false,

  resolveStyles(props, breakpoint) {
    const bgCss = colorValueToCss(props.backgroundColor, '#4f46e5');
    const bgSolid = colorValueToSolidCss(props.backgroundColor, '#4f46e5');
    const isOutline = props.variant === 'outline' || props.style === 'outline';

    const styles = {
      fontFamily: props.fontFamily ? getFontFamilyCSS(props.fontFamily) : undefined,
      background: isOutline ? 'transparent' : bgCss,
      color: isOutline ? bgSolid : colorValueToSolidCss(props.textColor, '#ffffff'),
      cursor: 'pointer',
      fontSize: props.fontSize
        ? getResponsiveDimension(props.fontSize, breakpoint)
        : (props.size === 'sm' ? '14px' : props.size === 'lg' ? '18px' : '16px'),
      fontWeight: props.fontWeight || '600',
      letterSpacing: props.letterSpacing ? `${props.letterSpacing}px` : undefined,
      transition: 'all 0.2s ease',
      display: 'inline-block',
      textDecoration: 'none',
    };

    // Override border based on style
    if (props.borderWidth > 0 && !isOutline) {
      styles.border = `${props.borderWidth}px ${props.borderStyle || 'solid'} ${colorValueToSolidCss(props.borderColor, 'transparent')}`;
    } else if (isOutline) {
      styles.border = `2px solid ${bgSolid}`;
    } else {
      styles.border = 'none';
    }

    return styles;
  },

  defaultProps: {
    // Content
    text: 'Click Here',
    url: '',
    openInNewTab: false,

    // Style
    variant: 'primary',
    size: 'md',
    fullWidth: false,

    // Colors
    backgroundColor: '#4f46e5',
    textColor: '#ffffff',
    hoverBackgroundColor: '#4338ca',

    // Typography
    fontFamily: 'System Default',
    fontSize: 16,
    fontWeight: '600',

    // Size
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 12, bottom: 12, left: 24, right: 24, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 8,

    // Advanced
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'text',
      type: 'text',
      label: 'Button Text',
      category: 'Content',
      default: 'Click Here',
    },
    {
      key: 'url',
      type: 'link',
      label: 'Link URL',
      category: 'Content',
      placeholder: 'https://...',
    },
    {
      key: 'openInNewTab',
      type: 'checkbox',
      label: 'Open in new tab',
      category: 'Content',
      default: false,
    },

    // Style category
    {
      key: 'variant',
      type: 'select',
      label: 'Variant',
      category: 'Style',
      default: 'primary',
      options: [
        { value: 'primary', label: 'Primary (Filled)' },
        { value: 'secondary', label: 'Secondary (Outline)' },
        { value: 'ghost', label: 'Ghost (Text only)' },
      ],
    },
    {
      key: 'size',
      type: 'select',
      label: 'Size',
      category: 'Style',
      default: 'md',
      options: [
        { value: 'sm', label: 'Small' },
        { value: 'md', label: 'Medium' },
        { value: 'lg', label: 'Large' },
        { value: 'xl', label: 'Extra Large' },
      ],
    },
    {
      key: 'fullWidth',
      type: 'checkbox',
      label: 'Full Width',
      category: 'Style',
      default: false,
    },

    // Colors category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Colors',
      default: '#4f46e5',
      allowGradient: true,
    },
    {
      key: 'textColor',
      type: 'color',
      label: 'Text Color',
      category: 'Colors',
      default: '#ffffff',
    },
    {
      key: 'hoverBackgroundColor',
      type: 'color',
      label: 'Hover Background',
      category: 'Colors',
      default: '#4338ca',
    },

    // Typography category
    {
      key: 'fontFamily',
      type: 'fontFamily',
      label: 'Font Family',
      category: 'Typography',
      default: 'System Default',
    },
    {
      key: 'fontSize',
      type: 'number',
      label: 'Font Size (px)',
      category: 'Typography',
      min: 10,
      max: 48,
      default: 16,
    },
    {
      key: 'fontWeight',
      type: 'select',
      label: 'Font Weight',
      category: 'Typography',
      default: '600',
      options: [
        { value: 'normal', label: 'Normal' },
        { value: '500', label: 'Medium' },
        { value: '600', label: 'Semibold' },
        { value: 'bold', label: 'Bold' },
      ],
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 10,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 50,
      default: 8,
    },

    // Advanced category
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default buttonComponent;
